/**
 * Rebecca Elizabeth — "The Curator" homepage.
 * Shared behavior for the curator-* sections: scroll reveal, header
 * transparency, founder typewriter, and the "Our World"
 * constellation. Framework-free, matches Dawn's custom-element style.
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ----------------------------------------------------------------
   * Scroll reveal — shared across every curator-* section.
   * Elements with .cura-reveal fade/rise in once scrolled into view.
   * Groups marked [data-cura-stagger] stagger their children by 0.11s.
   * ---------------------------------------------------------------- */
  function initReveal() {
    var items = document.querySelectorAll('.cura-reveal, .cura-reveal-soft');
    if (!items.length) return;

    document.querySelectorAll('[data-cura-stagger]').forEach(function (group) {
      var children = group.querySelectorAll('.cura-reveal, .cura-reveal-soft');
      children.forEach(function (el, i) {
        el.style.transitionDelay = (i * 0.11).toFixed(2) + 's';
      });
    });

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' }
    );
    items.forEach(function (el) {
      io.observe(el);
    });

    // Belt-and-suspenders: never leave content stuck hidden (e.g. already
    // in view on load, before the observer has fired once).
    var sweep = function (force) {
      var vh = window.innerHeight || 800;
      document.querySelectorAll('.cura-reveal:not(.is-in), .cura-reveal-soft:not(.is-in)').forEach(function (el) {
        var top = el.getBoundingClientRect().top;
        if (force || top < vh * 0.93) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    };
    sweep();
    window.setTimeout(function () {
      sweep(true);
    }, 1300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  /* ----------------------------------------------------------------
   * Product hero starfield — scatters faint twinkling dots behind the
   * dark-mood product hero. Same technique as the "Our World"
   * constellation's background field, scoped to its own class/keyframe.
   * ---------------------------------------------------------------- */
  function buildHeroField() {
    document.querySelectorAll('[data-cura-hero-field]').forEach(function (field) {
      if (field.childElementCount || prefersReducedMotion()) return;
      var html = '';
      for (var i = 0; i < 120; i++) {
        var x = (Math.random() * 100).toFixed(2);
        var y = (Math.random() * 100).toFixed(2);
        var s = (Math.random() * 1.6 + 0.5).toFixed(2);
        var o = (Math.random() * 0.5 + 0.15).toFixed(2);
        var dur = (Math.random() * 4 + 3).toFixed(1);
        var delay = (Math.random() * 5).toFixed(1);
        html +=
          '<span style="position:absolute; left:' + x + '%; top:' + y + '%; width:' + s + 'px; height:' + s +
          'px; border-radius:50%; background:#e0c39a; opacity:' + o +
          '; animation: cura-ph-bg-twinkle ' + dur + 's ease-in-out infinite; animation-delay:' + delay + 's;"></span>';
      }
      field.innerHTML = html;
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildHeroField);
  } else {
    buildHeroField();
  }
  // Belt-and-suspenders: a later, independent trigger in case the field
  // element isn't present yet at defer-script time for any reason (e.g.
  // Shopify's section-rendering API or a slow-rendering earlier section).
  window.addEventListener('load', buildHeroField);
  window.setTimeout(buildHeroField, 1200);

  /* ----------------------------------------------------------------
   * Collection hero starfield — bright 4-point glint/sparkle marks
   * (cross-shaped via ::before/::after), matching the collection page
   * design reference exactly. Deliberately a different, more visible
   * technique than the soft ambient dots buildHeroField() above uses
   * elsewhere — kept as a separate function/attribute so this doesn't
   * change behavior anywhere else in the theme.
   * ---------------------------------------------------------------- */
  function buildCollectionStarfield() {
    document.querySelectorAll('[data-cura-collection-field]').forEach(function (field) {
      if (field.childElementCount || prefersReducedMotion()) return;
      var html = '';
      for (var i = 0; i < 70; i++) {
        var x = (Math.random() * 100).toFixed(2);
        var y = (Math.random() * 100).toFixed(2);
        var s = (Math.random() * 9 + 6).toFixed(1);
        var delay = (Math.random() * 3.5).toFixed(2);
        html +=
          '<span class="cura-cg__star" style="left:' + x + '%; top:' + y + '%; width:' + s + 'px; height:' + s +
          'px; animation-delay:' + delay + 's;"></span>';
      }
      field.innerHTML = html;
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildCollectionStarfield);
  } else {
    buildCollectionStarfield();
  }
  window.addEventListener('load', buildCollectionStarfield);
  window.setTimeout(buildCollectionStarfield, 1200);

  /* ----------------------------------------------------------------
   * <curator-header> — fixed nav that fades from transparent (cream
   * text, over the hero) to solid parchment (ink text) on scroll.
   * ---------------------------------------------------------------- */
  class CuratorHeaderElement extends HTMLElement {
    connectedCallback() {
      this.logoWrap = this.querySelector('[data-cura-header-logo]');
      this.smallLogo = this.querySelector('[data-cura-header-small-logo]');
      this.transparent = this.hasAttribute('data-cura-transparent');
      this.ticking = false;

      // Mobile dropdown toggles (Shop / Campaign / Customer Service): hover
      // doesn't exist on touch, so tapping the trigger text itself expands
      // the submenu instead of navigating straight through. On desktop the
      // same tap still navigates normally (hover already reveals the menu
      // there), so this only intercepts the click below the 750px breakpoint.
      this.querySelectorAll('[data-cura-dropdown-toggle]').forEach(function (toggle) {
        toggle.addEventListener('click', function (event) {
          if (!window.matchMedia('(max-width: 749px)').matches) return;
          var dropdown = toggle.closest('.cura-header__dropdown');
          if (!dropdown) return;
          event.preventDefault();
          dropdown.classList.toggle('is-open');
        });
      });

      if (!this.transparent) return;

      this._handleScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this._handleScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      if (this._handleScroll) window.removeEventListener('scroll', this._handleScroll);
    }

    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;
      var self = this;
      requestAnimationFrame(function () {
        self.onScroll();
        self.ticking = false;
      });
    }

    onScroll() {
      var y = window.scrollY || 0;
      var vh = window.innerHeight || 800;
      var p = Math.max(0, Math.min(1, y / 190));

      if (this.logoWrap) {
        this.logoWrap.style.opacity = String(1 - p);
        this.logoWrap.style.maxHeight = (106 * (1 - p)).toFixed(1) + 'px';
        this.logoWrap.style.marginBottom = (16 * (1 - p)).toFixed(1) + 'px';
        this.logoWrap.style.transform = 'translateY(' + (-8 * p).toFixed(1) + 'px)';
      }

      var solid = y > vh * 0.82;
      this.classList.toggle('is-solid', solid);
      if (this.smallLogo) this.smallLogo.style.opacity = solid ? '1' : '0';
    }
  }
  if (!customElements.get('curator-header')) {
    customElements.define('curator-header', CuratorHeaderElement);
  }

  /* ----------------------------------------------------------------
   * <curator-founder> — typewriter quote (triggers on scroll-into-view).
   * ---------------------------------------------------------------- */
  class CuratorFounderElement extends HTMLElement {
    connectedCallback() {
      this.quote = this.querySelector('[data-cura-typewriter]');
      this._typed = false;

      if (this.quote && 'IntersectionObserver' in window) {
        var self = this;
        this._tio = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                self.typeQuote();
                self._tio.disconnect();
              }
            });
          },
          { threshold: 0.4 }
        );
        this._tio.observe(this.quote);
      } else if (this.quote) {
        this.typeQuote();
      }
    }

    disconnectedCallback() {
      if (this._tio) this._tio.disconnect();
      window.clearTimeout(this._typeTimer);
    }

    typeQuote() {
      if (this._typed || !this.quote) return;
      this._typed = true;
      var sizer = this.quote.querySelector('[data-cura-typewriter-sizer]');
      var typed = this.quote.querySelector('[data-cura-typewriter-typed]');
      if (!sizer || !typed) return;
      var full = sizer.textContent;

      if (prefersReducedMotion()) {
        typed.textContent = full;
        return;
      }

      var speed = parseInt(this.getAttribute('data-cura-type-speed'), 10) || 58;
      var frag = document.createDocumentFragment();
      full.split('').forEach(function (ch, i) {
        var span = document.createElement('span');
        span.className = 'cura-founder__char';
        span.style.transitionDelay = (i * speed) + 'ms';
        span.textContent = ch;
        frag.appendChild(span);
      });
      typed.appendChild(frag);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          typed.classList.add('is-typed');
        });
      });
    }
  }
  if (!customElements.get('curator-founder')) {
    customElements.define('curator-founder', CuratorFounderElement);
  }

  /* ----------------------------------------------------------------
   * <curator-constellation> — "Our World" hover portal.
   * Each star has its own pre-rendered teardrop card pinned beside it
   * (positioned server-side via Liquid, since x/y are fixed per star).
   * JS only toggles the active state and draws the connecting line —
   * no repositioning math needed at runtime.
   * ---------------------------------------------------------------- */
  class CuratorConstellationElement extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.stars = Array.prototype.slice.call(this.querySelectorAll('[data-cura-ow-star]'));
      this._deactivateTimer = null;
      this._active = null;

      this.stars.forEach(function (star) {
        star.addEventListener('mouseenter', function () {
          self.activate(star);
        });
        star.addEventListener('focus', function () {
          self.activate(star);
        });
        star.addEventListener('mouseleave', function () {
          self.scheduleDeactivate();
        });
        star.addEventListener('blur', function () {
          self.scheduleDeactivate();
        });

        var key = star.getAttribute('data-cura-ow-star');
        var card = self.querySelector('[data-cura-ow-card="' + key + '"]');
        if (card) {
          card.addEventListener('mouseenter', function () {
            window.clearTimeout(self._deactivateTimer);
          });
          card.addEventListener('mouseleave', function () {
            self.scheduleDeactivate();
          });
          var closeBtn = card.querySelector('[data-cura-ow-close]');
          if (closeBtn) {
            closeBtn.addEventListener('click', function (e) {
              e.preventDefault();
              self.deactivate(star);
              self._active = null;
            });
          }
        }
      });

      this.buildStarfield();
    }

    activate(star) {
      window.clearTimeout(this._deactivateTimer);
      if (this._active && this._active !== star) this.deactivate(this._active);
      this._active = star;
      star.classList.add('is-active');

      var key = star.getAttribute('data-cura-ow-star');
      var card = this.querySelector('[data-cura-ow-card="' + key + '"]');
      var line = this.querySelector('[data-cura-ow-line="' + key + '"]');
      if (card) card.classList.add('is-open');
      if (line) this.drawLine(line);
    }

    scheduleDeactivate() {
      var self = this;
      window.clearTimeout(this._deactivateTimer);
      this._deactivateTimer = window.setTimeout(function () {
        if (self._active) self.deactivate(self._active);
        self._active = null;
      }, 240);
    }

    deactivate(star) {
      star.classList.remove('is-active');
      var key = star.getAttribute('data-cura-ow-star');
      var card = this.querySelector('[data-cura-ow-card="' + key + '"]');
      var line = this.querySelector('[data-cura-ow-line="' + key + '"]');
      if (card) card.classList.remove('is-open');
      if (line) this.retractLine(line);
    }

    lineLength(line) {
      if (!line._curaLen) {
        try {
          line._curaLen = line.getTotalLength();
        } catch (e) {
          line._curaLen = 0;
        }
      }
      return line._curaLen;
    }

    drawLine(line) {
      window.clearTimeout(line._curaFadeTimer);
      line.classList.add('is-lit');
      if (prefersReducedMotion()) {
        line.style.strokeDashoffset = '0';
        return;
      }
      var len = this.lineLength(line);
      line.style.strokeDasharray = len + ' ' + len;
      line.style.strokeDashoffset = String(len);
      requestAnimationFrame(function () {
        line.style.strokeDashoffset = '0';
      });
    }

    retractLine(line) {
      var len = this.lineLength(line);
      line.style.strokeDashoffset = String(len);
      window.clearTimeout(line._curaFadeTimer);
      line._curaFadeTimer = window.setTimeout(function () {
        line.classList.remove('is-lit');
      }, 2400);
    }

    buildStarfield() {
      var field = this.querySelector('[data-cura-ow-field]');
      if (!field || field.childElementCount || prefersReducedMotion()) return;
      var html = '';
      for (var i = 0; i < 70; i++) {
        var x = (Math.random() * 100).toFixed(2);
        var y = (Math.random() * 100).toFixed(2);
        var s = (Math.random() * 1.6 + 0.6).toFixed(2);
        var o = (Math.random() * 0.5 + 0.2).toFixed(2);
        var tw = (Math.random() * 4 + 3).toFixed(1);
        var d = (Math.random() * 5).toFixed(1);
        html +=
          '<span class="cura-ow-bg-star" style="left:' +
          x +
          '%; top:' +
          y +
          '%; width:' +
          s +
          'px; height:' +
          s +
          'px; opacity:' +
          o +
          '; animation-duration:' +
          tw +
          's; animation-delay:' +
          d +
          's;"></span>';
      }
      field.innerHTML = html;
    }
  }
  if (!customElements.get('curator-constellation')) {
    customElements.define('curator-constellation', CuratorConstellationElement);
  }

  /* ----------------------------------------------------------------
   * <curator-custom-sizing> — reveals the commission panel whenever
   * the REAL selected variant's size option matches the configured
   * "Custom" value (data-custom-value). Driven by Dawn's own
   * PUB_SUB_EVENTS.variantChange (assets/product-info.js /
   * assets/pubsub.js, both global scripts already loaded on every
   * page), not a separate manual toggle — "Custom" is a genuine
   * product variant, so selecting it in the normal size picker is
   * what should open this panel. Its inputs live outside the real
   * <form> and are associated via the HTML form="" attribute, so no
   * submission wiring is needed here.
   * ---------------------------------------------------------------- */
  class CuratorCustomSizingElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-cura-custom-panel]');
      if (!this.panel) return;

      this.customValue = this.getAttribute('data-custom-value') || 'Custom';
      this.sectionId = this.getAttribute('data-section');

      // Dawn replaces the entire <variant-selects> element via
      // HTMLUpdateUtility.viewTransition on every change (fetched fresh
      // from the server), which wipes any listener attached to that
      // specific node, and its async pub/sub event can be unreliable in
      // preview contexts. So: listen on document (delegation, immune to
      // the swap since document itself is never replaced) and react
      // SYNCHRONOUSLY to the raw click/change — don't wait on any Dawn
      // event at all.
      this._onDocChange = this.onDocChange.bind(this);
      document.addEventListener('change', this._onDocChange, true);
      document.addEventListener('click', this._onDocChange, true);

      this.moveCustomPillToEnd();
      this.syncFromCurrentSelects();

      // Re-run the reorder whenever <variant-selects> is actually
      // replaced in the DOM (Dawn's AJAX swap) — watching for the real
      // mutation, rather than guessing at a delay, means this can never
      // race the swap regardless of how long the fetch takes.
      var selectsParent = document.querySelector('variant-selects') && document.querySelector('variant-selects').parentElement;
      if (selectsParent && 'MutationObserver' in window) {
        var self = this;
        this._observer = new MutationObserver(function () {
          self.moveCustomPillToEnd();
          self.syncFromCurrentSelects();
        });
        this._observer.observe(selectsParent, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onDocChange, true);
      document.removeEventListener('click', this._onDocChange, true);
      if (this._observer) this._observer.disconnect();
    }

    onDocChange(event) {
      var target = event.target;
      if (!target || target.tagName !== 'INPUT' || target.type !== 'radio') return;
      if (!target.closest('variant-selects')) return;
      this.applyState(target.value === this.customValue);
    }

    moveCustomPillToEnd() {
      var selects = document.querySelector('variant-selects');
      if (!selects) return;
      var input = selects.querySelector('input[type="radio"][value="' + this.customValue + '"]');
      if (!input) return;
      var label = selects.querySelector('label[for="' + input.id + '"]');
      var fieldset = input.closest('fieldset');
      if (!fieldset) return;

      // Guard: if already the last child, do nothing. appendChild() on a
      // node that's already in the tree still fires a childList mutation
      // even when its position doesn't change — without this check, the
      // MutationObserver below re-triggers this method, which re-appends,
      // which re-triggers the observer, forever (freezes the page).
      var expectedLast = label || input;
      if (fieldset.lastElementChild === expectedLast) return;

      fieldset.appendChild(input);
      if (label) fieldset.appendChild(label);
    }

    syncFromCurrentSelects() {
      var selects = document.querySelector('variant-selects');
      var checked = selects && selects.querySelector('input[type="radio"]:checked');
      if (checked) this.applyState(checked.value === this.customValue);
    }

    applyState(isCustom) {
      this.panel.classList.toggle('is-open', isCustom);

      var submitLabel = document.querySelector('#ProductSubmitButton-' + this.sectionId + ' span');
      if (submitLabel) {
        if (isCustom) {
          submitLabel.dataset.curaOriginalLabel = submitLabel.dataset.curaOriginalLabel || submitLabel.textContent;
          submitLabel.textContent = this.getAttribute('data-commission-label') || 'Begin this commission';
        } else if (submitLabel.dataset.curaOriginalLabel) {
          submitLabel.textContent = submitLabel.dataset.curaOriginalLabel;
        }
      }
    }
  }
  if (!customElements.get('curator-custom-sizing')) {
    customElements.define('curator-custom-sizing', CuratorCustomSizingElement);
  }

  /* ----------------------------------------------------------------
   * <curator-drawer> — generic slide-in panel, used for both the
   * size-guide and contact drawers. Any element with
   * [data-cura-drawer-open="<id>"] opens the <curator-drawer
   * data-cura-drawer-id="<id>"> instance with a matching id. Borrows
   * the focus-trap helpers Dawn's own cart drawer already relies on
   * (assets/global.js) rather than reimplementing them.
   * ---------------------------------------------------------------- */
  class CuratorDrawerElement extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.id = this.getAttribute('data-cura-drawer-id');
      this.panel = this.querySelector('[data-cura-drawer-panel]');
      this.scrim = this.querySelector('[data-cura-drawer-scrim]');
      this.closeBtn = this.querySelector('[data-cura-drawer-close]');

      this._openers = Array.prototype.slice.call(
        document.querySelectorAll('[data-cura-drawer-open="' + this.id + '"]')
      );
      this._openers.forEach(function (opener) {
        opener.addEventListener('click', function () {
          self.open(opener);
        });
      });

      if (this.scrim) this.scrim.addEventListener('click', this.close.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.close.bind(this));

      var chooseCustomBtn = this.querySelector('[data-cura-choose-custom]');
      if (chooseCustomBtn) {
        chooseCustomBtn.addEventListener('click', function () {
          var customValue = chooseCustomBtn.getAttribute('data-custom-value') || 'Custom';
          var selects = document.querySelector('variant-selects');
          var input = selects && selects.querySelector('input[type="radio"][value="' + customValue + '"]');
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          self.close();
        });
      }

      this._onKeydown = function (e) {
        if (e.key === 'Escape' && self.classList.contains('is-open')) self.close();
      };
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
    }

    open(triggeredBy) {
      this._triggeredBy = triggeredBy;
      this.classList.add('is-open');
      document.addEventListener('keydown', this._onKeydown);
      if (window.trapFocus && this.panel) window.trapFocus(this.panel);
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onKeydown);
      if (window.removeTrapFocus) window.removeTrapFocus(this._triggeredBy);
      document.body.style.overflow = '';
    }
  }
  if (!customElements.get('curator-drawer')) {
    customElements.define('curator-drawer', CuratorDrawerElement);
  }

  /* ----------------------------------------------------------------
   * <curator-study-drawer> — inline-expanding accordion for the
   * "Study the Piece" section. Toggles a max-height transition and
   * rotates the "+" knob, mirroring the reference design's behavior.
   * ---------------------------------------------------------------- */
  class CuratorStudyDrawerElement extends HTMLElement {
    connectedCallback() {
      this.head = this.querySelector('[data-cura-study-head]');
      this.body = this.querySelector('[data-cura-study-body]');
      if (!this.head || !this.body) return;

      var self = this;
      this.head.addEventListener('click', function () {
        self.classList.toggle('is-open');
      });
    }
  }
  if (!customElements.get('curator-study-drawer')) {
    customElements.define('curator-study-drawer', CuratorStudyDrawerElement);
  }

  /* ----------------------------------------------------------------
   * <curator-buy-bar> — persistent bottom bar that appears once the
   * page has scrolled roughly past the hero, into the buy panel
   * (product photos + size selection) — matches the design reference's
   * own trigger (scrollY > 72% of viewport height), not "buy panel has
   * left the viewport". Its submit control uses form="" to target the
   * ONE real product form; it never renders a second <form>.
   *
   * The bar carries its own size <select> (data-cura-bar-option-name)
   * so shoppers can choose a size without scrolling back up — it isn't
   * a real form field. Changing it drives the matching control inside
   * the real <variant-selects> (Dawn's product-info.js owns the actual
   * variant lookup, price refresh and the hidden id input), and a
   * document-level 'change' listener below keeps the bar in sync the
   * other way whenever the size is changed from the main picker — that
   * picker's DOM node gets replaced on every change (product-info.js
   * swaps it in from a server-rendered fragment), so the bar can't just
   * hold a long-lived reference to it.
   * ---------------------------------------------------------------- */
  class CuratorBuyBarElement extends HTMLElement {
    connectedCallback() {
      this.ticking = false;
      this._handleScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this._handleScroll, { passive: true });
      this.onScroll();

      this.sizeSelect = this.querySelector('[data-cura-bar-option-name]');
      if (this.sizeSelect) {
        this._handleSizeChange = this.handleSizeChange.bind(this);
        this.sizeSelect.addEventListener('change', this._handleSizeChange);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._handleScroll);
      if (this.sizeSelect) this.sizeSelect.removeEventListener('change', this._handleSizeChange);
    }

    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;
      var self = this;
      requestAnimationFrame(function () {
        self.onScroll();
        self.ticking = false;
      });
    }

    onScroll() {
      var y = window.scrollY || 0;
      var vh = window.innerHeight || 800;
      this.classList.toggle('is-visible', y > vh * 0.72);
    }

    handleSizeChange(event) {
      var optionName = this.sizeSelect.dataset.curaBarOptionName;
      var value = event.target.value;
      var variantSelects = document.getElementById('variant-selects-' + this.dataset.section);
      if (!variantSelects) return;

      var match = null;
      var candidates = variantSelects.querySelectorAll('[data-option-name="' + optionName + '"]');
      for (var i = 0; i < candidates.length; i++) {
        if (candidates[i].value === value) {
          match = candidates[i];
          break;
        }
      }
      if (!match) return;

      if (match.tagName === 'OPTION') {
        var parentSelect = match.closest('select');
        if (!parentSelect) return;
        parentSelect.value = value;
        parentSelect.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
  if (!customElements.get('curator-buy-bar')) {
    customElements.define('curator-buy-bar', CuratorBuyBarElement);
  }

  // Mirror the main variant picker's option changes back into any
  // matching curator-buy-bar size select, so the bar stays correct
  // when the shopper changes size from the full picker instead of the
  // bar itself. Delegated on document because product-info.js replaces
  // the <variant-selects> node (and everything inside it) on every
  // change, so a direct listener on it would stop firing after the
  // first swap.
  document.addEventListener('change', function (event) {
    var target = event.target;
    var optionName;
    var value;

    if (target.tagName === 'SELECT') {
      var selectedOption = target.selectedOptions && target.selectedOptions[0];
      if (!selectedOption) return;
      optionName = selectedOption.dataset.optionName;
      value = selectedOption.value;
    } else {
      optionName = target.dataset ? target.dataset.optionName : null;
      value = target.value;
    }
    if (!optionName) return;

    var variantSelects = target.closest('variant-selects');
    if (!variantSelects) return;

    var sectionId = variantSelects.id.replace(/^variant-selects-/, '');
    var barSelect = document.querySelector(
      'curator-buy-bar[data-section="' + sectionId + '"] [data-cura-bar-option-name="' + optionName + '"]'
    );
    if (barSelect && barSelect.value !== value) barSelect.value = value;
  });

  /* ----------------------------------------------------------------
   * Contact drawer — video consultation checkbox. This form is a
   * plain native Shopify contact form (full page submit, no AJAX), so
   * a single document-level submit listener is enough: fold the
   * request into the message body and tags right before it submits,
   * so it's guaranteed to reach the merchant however the store's
   * contact notification template is set up.
   * ---------------------------------------------------------------- */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var checkbox = form.querySelector('[data-cura-consult-check]');
    if (!checkbox || !checkbox.checked) return;

    var body = form.querySelector('textarea[name="contact[body]"]');
    if (body) {
      var note = "P.S. I'd also like to book a 15-minute video consultation.";
      body.value = body.value.trim() ? body.value + '\n\n' + note : note;
    }
    var tags = form.querySelector('[data-cura-consult-tags]');
    if (tags) tags.value += ', video-consultation-request';
  });

  /* ----------------------------------------------------------------
   * <curator-collection-view-toggle> — the Outfit/Product switch on
   * collection pages. Sets data-cura-view on <html> (not just its own
   * subtree) so curator-collection-grid's CSS, in a different section
   * entirely, can react to it via a plain attribute selector — no
   * cross-section JS wiring needed.
   * ---------------------------------------------------------------- */
  class CuratorCollectionViewToggleElement extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.querySelectorAll('[data-cura-view-btn]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var view = btn.getAttribute('data-cura-view-btn');
          if (view === 'outfit') {
            document.documentElement.removeAttribute('data-cura-view');
          } else {
            document.documentElement.setAttribute('data-cura-view', view);
          }
          self.querySelectorAll('[data-cura-view-btn]').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });
        });
      });
    }
  }
  if (!customElements.get('curator-collection-view-toggle')) {
    customElements.define('curator-collection-view-toggle', CuratorCollectionViewToggleElement);
  }
})();
